import React, { createContext, useState, useContext, useEffect } from 'react';

// Create a context
const PipelineContext = createContext();

// Default session state
const defaultSession = {
  pipeline_id: null,
  pipeline_name: "Unsaved_Pipeline",
  owner_email: null,
  owner_user_id: null,
  access: null,
  last_run_status: "notRun",
  last_run_id: null,
  nodes: [],
  selected_node: null, // Add selected_node key with default value null
  last_run_nodes: [] // Array to store nodes that were run in the last pipeline execution
};

// Create a provider component
export const PipelineProvider = ({ children }) => {
  const [session, setSession] = useState(defaultSession);
  const [dirtyNodeInputs, setDirtyNodeInputs] = useState({});

  // Log session changes
  useEffect(() => {
    console.log("Session state changed:", session);
  }, [session]);

  // Function to initialize the pipeline
  const initialize_pipeline = (pipelineName, metadata = {}) => {
    console.log(`Initializing pipeline: ${pipelineName}`);
    // Reset the session but keep the pipeline name
    // This will clear any existing nodes
    setSession({
      ...defaultSession,
      pipeline_id: metadata.pipeline_id || metadata.id || metadata._id || null,
      pipeline_name: pipelineName,
      owner_email: metadata.owner_email || metadata.ownerEmail || null,
      owner_user_id: metadata.owner_user_id || metadata.ownerUserId || null,
      access: metadata.access || null,
      nodes: [], // Explicitly clear nodes
      last_run_nodes: [] // Reset the last run nodes array
    });
    setDirtyNodeInputs({});
    
    // Note: The actual loading of nodes is handled in the Canvas component
    // when it detects a change in the pipeline name
  };
  
  // Function to batch add nodes to the session
  // This is used when loading a pipeline to avoid multiple state updates
  const batchAddNodes = (nodes) => {
    if (!nodes || nodes.length === 0) return;
    
    setSession(prevSession => {
      // Create new nodes array with all the nodes to add
      const newNodes = nodes.map(node => {
        // Base node structure
        const baseNode = {
          node_name: node.name,
          node_type: node.nodeType || 'input', // Use the provided nodeType or default to 'input'
          is_configured: node.isConfigured || true,
          position: node.position || { x: 0, y: 0 },
        };
        
        // Add node-specific parameters
        if (node.nodeType === 'normalizer') {
          return {
            ...baseNode,
            node_params: [
              {
                target: node.target,
                interval: node.interval
              }
            ]
          };
        } else if (node.nodeType === 'scaler') {
          return {
            ...baseNode,
            node_params: [
              {
                mult_factor: node.multipleFactor,
                col_name: node.colName
              }
            ]
          };
        } else if (node.nodeType === 'capper') {
          return {
            ...baseNode,
            node_params: [
              {
                acdc_ratio: node.acdcRatio,
                col_name: node.colName,
                KA: node.KA,
                MH: node.MH,
                gen_to_trans_loss: node.genToTransLoss,
                cap_limit: node.capLimit
              }
            ]
          };
        } else if (node.nodeType === 'loss') {
          return {
            ...baseNode,
            node_params: [
              {
                loss_perc: node.lossPerc,
                col_name: node.colName
              }
            ]
          };
        } else if (node.nodeType === 'adder') {
          return {
            ...baseNode,
            node_params: [
              {
                inputA_col_name: node.inputA_col_name,
                inputB_col_name: node.inputB_col_name,
                sum_column_name: node.sum_column_name
              }
            ]
          };
        } else if (node.nodeType === 'matcher') {
          return {
            ...baseNode,
            node_params: [
              {
                cons_col_name: node.cons_col_name,
                gen_col_name: node.gen_col_name
              }
            ]
          };
        } else if (node.nodeType === 'settle') {
          return {
            ...baseNode,
            node_params: [
              {
                source_label: node.sourceLabel,
                banking_type: node.bankingType,
                banking_fee: node.bankingFee,
                generation_file_name: node.generationFileName
              }
            ]
          };
        } else if (node.nodeType === 'consumption') {
          const params = Array.isArray(node.node_params) ? (node.node_params[0] || {}) : {};
          return {
            ...baseNode,
            node_params: [
              {
                file_name: node.fileName ?? params.file_name,
                consumption_input_mode: node.inputMode ?? params.consumption_input_mode,
                consumption_year: node.year ?? params.consumption_year,
                consumption_preview: node.preview ?? params.consumption_preview,
                consumption_banking_policy: node.bankingPolicyName ?? params.consumption_banking_policy,
                existing_oa_re_units: node.existingOaReUnits ?? params.existing_oa_re_units ?? 0,
                existing_btm_units: node.existingBtmUnits ?? params.existing_btm_units ?? 0,
                existing_oa_type: node.existingOaType ?? params.existing_oa_type ?? 'solar',
                existing_oa_input_mode: node.existingOaInputMode ?? params.existing_oa_input_mode ?? 'annual',
                existing_oa_hourly_filename: node.existingOaFileName ?? params.existing_oa_hourly_filename ?? '',
                balance_mode: node.balanceMode ?? node.balance_mode ?? params.balance_mode ?? params.balanceMode,
                units: node.units ?? params.units
              }
            ]
          };
        } else if (node.nodeType === 'windgeneration') {
          return {
            ...baseNode,
            node_params: [
              {
                interval: node.interval,
                file_path: node.file_path || `./gen_upload/${node.node_name}.csv`,
                mw_per_turbine: node.mw_per_turbine ?? 2.1,
              }
            ]
          };
        } else if (node.nodeType === 'solargeneration') {
          return {
            ...baseNode,
            node_params: [
              {
                interval: node.interval,
                file_path: node.file_path || `./gen_upload/${node.node_name}.csv`,
                // Persist BTM flag for solar generation nodes
                Btm_flag: node.btmFlag ?? node.Btm_flag ?? false
              }
            ]
          };
        }else if(node.nodeType ==='extrapolation') {
          return {
            ...baseNode,
            node_params: [
              {
                years: node.years,
                bankable_years: node.bankableYears,
                wind_dep_perc: node.windDepPerc ?? 0,
                solar_dep_perc: node.solarDepPerc ?? 0.5,
                banking_charge: node.bankingCharge ?? 8,
                tot_wind_gen_col: node.totWindGenCol ?? '',
                tot_solar_gen_col: node.totSolarGenCol ?? '',
                cons_app_perc: node.consAppPerc ?? 0,
                t_cons_col: node.tConsCol ?? ''
              }
            ]
          };
        } else if (node.nodeType === 'finance') {
          return {
            ...baseNode,
            node_params: [
              {
                per_unit_discom_cost: node.perUnitDiscomCost
              }
            ]
          };
        } else if (node.nodeType === 'loan') {
          return {
            ...baseNode,
            node_params: [
              {
                loan_amount: node.loanAmount,
                annual_rate: node.annualRate,
                tenure_years: node.tenureYears,
                moratorium_years: node.moratoriumYears
              }
            ]
          };
        } else if (node.nodeType === 'depreciation') {
          return {
            ...baseNode,
            node_params: [
              {
                total_hybrid_capex: node.totalHybridCapex,
                depreciation_rate: node.depreciationRate,
                tax_rate: node.taxRate,
                gst_input: node.gstInput,
                gst_perc: node.gstPerc
              }
            ]
          };
        } else if (node.nodeType === 'battery') {
          return {
            ...baseNode,
            node_params: [
              {
                input_node_name: node.inputNodeName,
                gen_col_name: node.genColName,
                battery_hours: node.batteryHours,
                battery_capacity_kw: node.batteryCapacityKw,
                round_trip_efficiency: node.roundTripEfficiency,
                dod: node.dod,
                battery_capex_per_kwh: node.batteryCapexPerKwh,
                battery_om_perc: node.batteryOmPerc,
                loan_perc: node.loanPerc,
                annual_rate: node.annualRate,
                tenure_years: node.tenureYears,
                dep_rate_for_IT_WDV: node.depRateForITWDV,
                addn_dep_rate: node.addnDepRate,
                tax_rate: node.taxRate,
                gst_input: node.gstInput,
                gst_perc: node.gstPerc,
                battery_assumptions: node.batteryAssumptions,
                charge_time_slots: node.chargeTimeSlots,
                discharge_time_slots: node.dischargeTimeSlots,
              }
            ]
          };
        } else if (node.nodeType === 'operationalcosts') {
          return {
            ...baseNode,
            node_params: [
              {
                no_of_bop_year: node.noOfBopYear,
                // Support both camelCase from UI and snake_case from loader
                wind_bop_escalation_perc: node.windBopEscalationPerc ?? node.wind_bop_escalation_perc,
                wind_escalation_perc: node.windEscalationPerc ?? node.wind_escalation_perc,
                solar_escalation_perc: node.solarEscalationPerc ?? node.solar_escalation_perc,
                discount_rate: node.discountRate,
                annual_maintenance_per_turbine_in_lakhs: node.annualMaintenancePerTurbineInLakhs,
                annual_maintenance_per_turbine_in_lakhs_with_bop: node.annualMaintenancePerTurbineInLakhsWithBop,
                annual_maintenance_per_solar_unit_in_lakhs: node.annualMaintenancePerSolarUnitInLakhs,
                insurance_percent_of_asset_cost: node.insurancePercentOfAssetCost,
                operational_charges: node.operationalCharges,
                asset_management_fees: node.assetManagementFees,
                annual_lease_charge_per_acre: node.annualLeaseChargePerAcre,
                annual_lease_escalation_perc: node.annualLeaseEscalationPerc,
                solar_acres_per_MWp: node.solarAcresPerMWp,
                wind_acres_per_WTG: node.windAcresPerWTG,
                first_year_benifits: node.firstYearBenifits,
                revenue_from_excess_banked_units_per_unit: node.revenueFromExcessBankedUnitsPerUnit,
                total_lapsed_units_for_excess_revenue_perc: node.totalLapsedUnitsForExcessRevenuePerc,
                transmission_charges_per_KWh: node.transmissionChargesPerKWh,
                wheeling_charges: node.wheelingCharges,
                electricity_duty_per_unit: node.electricityDutyPerUnit,
                banking_charge_rs: node.bankingChargeRs ?? node.banking_charge_rs ?? 0,
                per_KW: node.perKW,
                per_KWh: node.perKWh,
                transmission_charges_per_Kw_per_month: node.transmissionChargesPerKwPerMonth
              }
            ]
          };
        }else {
          // Default to input node parameters
          return {
            ...baseNode,
            node_params: [
              {
                type: node.type,
                interval: node.interval
              }
            ]
          };
        }
      });
      
      console.log('Batch adding nodes with types:', newNodes.map(n => n.node_type));
      
      // Log specific information about wind and solar generation nodes
      const windNodes = newNodes.filter(n => n.node_type === 'windgeneration');
      const solarNodes = newNodes.filter(n => n.node_type === 'solargeneration');
      
      if (windNodes.length > 0) {
        console.log('Adding wind generation nodes to session:', windNodes);
      }
      
      if (solarNodes.length > 0) {
        console.log('Adding solar generation nodes to session:', solarNodes);
      }
      
      return {
        ...prevSession,
        nodes: [...newNodes] // Replace all nodes with the new batch
      };
    });
  };

  // Function to run the pipeline
  const run_pipeline = (status, runNodes = [], runId = null) => {
    setSession((prevSession) => ({
      ...prevSession,
      last_run_status: status,
      last_run_nodes: runNodes,
      last_run_id: runId
    }));
  };

  // Insert new nodes or replace existing ones with same name/type.
  // Reconfigure/edit flows call add* helpers repeatedly; upsert avoids duplicates.
  const upsertSessionNode = (newNode) => {
    setSession((prevSession) => {
      const existingIdx = prevSession.nodes.findIndex(
        (node) => node.node_name === newNode.node_name
      );

      if (existingIdx === -1) {
        return {
          ...prevSession,
          nodes: [...prevSession.nodes, newNode]
        };
      }

      const nextNodes = [...prevSession.nodes];
      nextNodes[existingIdx] = {
        ...nextNodes[existingIdx],
        ...newNode
      };

      return {
        ...prevSession,
        nodes: nextNodes
      };
    });
  };

  // Track nodes whose upstream input links changed and need reconfiguration
  const markNodeDirty = (nodeName, payload = {}) => {
    if (!nodeName) return;
    setDirtyNodeInputs((prev) => ({
      ...prev,
      [nodeName]: {
        nodeName,
        ...prev[nodeName],
        ...payload,
        markedAt: Date.now()
      }
    }));
  };

  const clearNodeDirty = (nodeName) => {
    if (!nodeName) return;
    setDirtyNodeInputs((prev) => {
      if (!prev[nodeName]) return prev;
      const next = { ...prev };
      delete next[nodeName];
      return next;
    });
  };

  const clearAllDirtyNodes = () => {
    setDirtyNodeInputs({});
  };

  // Function to add an input node to the session
  const addInputNode = (nodeName, type, interval, position) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'input',
      is_configured: false,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          type: type,
          interval: interval
        }
      ]
    };
    upsertSessionNode(newNode);
  };

  // Function to set a node's configuration state
  const setNodeConfigured = (nodeName, isConfigured = true) => {
    setSession((prevSession) => ({
      ...prevSession,
      nodes: prevSession.nodes.map(node =>
        node.node_name === nodeName ? { ...node, is_configured: isConfigured } : node
      )
    }));
  };

  // Function to update the selected node
  const updateSelectedNode = (nodeName) => {
    setSession((prevSession) => ({
      ...prevSession,
      selected_node: nodeName
    }));
  };

  // Function to update a node's position
  const updateNodePosition = (nodeName, position) => {
    setSession((prevSession) => ({
      ...prevSession,
      nodes: prevSession.nodes.map(node =>
        node.node_name === nodeName ? { ...node, position } : node
      )
    }));
  };

  // Function to add a normalizer node to the session
  const addNormalizerNode = (nodeName, target, inputNodeName, position) => {
    // Convert target to interval format for UI display
    const interval = target === '15' ? '15 mins' : '1 hr';
    
    const newNode = {
      node_name: nodeName,
      node_type: 'normalizer',
      is_configured: true,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          target: target,
          interval: interval
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };

  // Function to add a scaler node to the session
  const addScalerNode = (nodeName, multipleFactor, colName, inputNodeName, position) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'scaler',
      is_configured: true,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          mult_factor: multipleFactor,
          col_name: colName
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };
  
  // Function to add a capper node to the session
  const addCapperNode = (
    nodeName,
    cappingLimitOrAcdcRatio,
    colName,
    inputNodeName,
    position,
    options = {}
  ) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'capper',
      is_configured: true,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          // Maintain backward compat: store under acdc_ratio going forward
          acdc_ratio: cappingLimitOrAcdcRatio,
          col_name: colName,
          KA: options.KA ?? null,
          MH: options.MH ?? null,
          gen_to_trans_loss: options.genToTransLoss ?? null,
          cap_limit: options.capLimit ?? null
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };
  
  // Function to add a loss subtractor node to the session
  const addLossSubtractorNode = (nodeName, lossPerc, colName, inputNodeName, position) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'loss',
      is_configured: true,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          // keep legacy aggregate value for session serialization compatibility
          loss_perc: lossPerc,
          col_name: colName
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };
  
  // Function to add an adder node to the session
  const addAdderNode = (nodeName, inputA_col_name, inputB_col_name, sum_column_name, inputNode1Name, inputNode2Name, position) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'adder',
      is_configured: true,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          inputA_col_name: inputA_col_name,
          inputB_col_name: inputB_col_name,
          sum_column_name: sum_column_name
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };
  
  // Function to add a matcher node to the session
  const addMatcherNode = (nodeName, cons_col_name, gen_col_name, cons_node_name, gen_node_name, position) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'matcher',
      is_configured: true,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          cons_col_name: cons_col_name,
          gen_col_name: gen_col_name
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };
  
  // Function to add a consumption node to the session
  const addConsumptionNode = (nodeName, fileName, file, position, options = {}) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'consumption',
      is_configured: true,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          file_name: fileName,
                consumption_input_mode: options.inputMode,
                consumption_year: options.year,
                consumption_preview: options.preview,
                consumption_banking_policy: options.bankingPolicyName,
                existing_oa_re_units: options.existingOaReUnits ?? 0,
                existing_btm_units: options.existingBtmUnits ?? 0,
                existing_oa_type: options.existingOaType ?? 'solar',
                existing_oa_input_mode: options.existingOaInputMode ?? 'annual',
                existing_oa_hourly_filename: options.existingOaFileName ?? '',
                balance_mode: options.balanceMode,
                units: options.units,
                slot_monthly_data: options.slotMonthlyData
            }
          ]
        };
    
    upsertSessionNode(newNode);
  };
  
  // Function to add a wind generation node to the session
  const addWindGenerationNode = (nodeName, interval, position, file_path, mw_per_turbine = 2.1) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'windgeneration',
      is_configured: true,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          interval: interval,
          file_path: file_path || `./gen_upload/${nodeName}.csv`,
          mw_per_turbine,
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };
  
  // Function to add a solar generation node to the session
  const addSolarGenerationNode = (nodeName, interval, position, file_path, btmFlag = false) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'solargeneration',
      is_configured: true,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          interval: interval,
          file_path: file_path || `./gen_upload/${nodeName}.csv`,
          // Store BTM flag in node params so it round-trips through backend catalog
          Btm_flag: btmFlag
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };
  
  // Function to add an extrapolation node to the session
  const addExtrapolationNode = (nodeName, years, bankableYears, inputNodeName, position, windDepPerc = 0, solarDepPerc = 0.5, bankingCharge = 8, totWindGenCol = '', totSolarGenCol = '', consAppPerc = 0, tConsCol = '') => {
    const newNode = {
      node_name: nodeName,
      node_type: 'extrapolation',
      is_configured: true,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          years: years,
          bankable_years: bankableYears,
          wind_dep_perc: windDepPerc,
          solar_dep_perc: solarDepPerc,
          banking_charge: bankingCharge,
          tot_wind_gen_col: totWindGenCol,
          tot_solar_gen_col: totSolarGenCol,
          cons_app_perc: consAppPerc,
          t_cons_col: tConsCol
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };
  
  // Function to add a banking node to the session
  const addBankingNode = (nodeName, bankingType, inputNodeName, position) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'banking',
      is_configured: true,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          banking_type: bankingType
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };
  
  // Function to add a settle node (one existing source in a priority chain) to the session
  const addSettleNode = (nodeName, inputNodeName, position, options = {}) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'settle',
      is_configured: true,
      position: position || { x: 0, y: 0 },
      node_params: [
        {
          input_node_name: inputNodeName,
          source_label: options.sourceLabel,
          banking_type: options.bankingType,
          banking_fee: options.bankingFee,
          generation_file_name: options.generationFileName
        }
      ]
    };

    upsertSessionNode(newNode);
  };

  // Function to add a finance node to the session (cash_equity removed)
  const addFinanceNode = (nodeName, perUnitDiscomCost, inputNodeName, position) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'finance',
      is_configured: true, // Start as unconfigured for new nodes
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          per_unit_discom_cost: perUnitDiscomCost,
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };

  // Update displayed computed discom tariff for a finance node
  const updateFinanceTariff = (nodeName, perUnitDiscomCost) => {
    setSession((prevSession) => ({
      ...prevSession,
      nodes: prevSession.nodes.map(node =>
        node.node_name === nodeName && node.node_type === 'finance'
          ? {
              ...node,
              node_params: [{ per_unit_discom_cost: perUnitDiscomCost }]
            }
          : node
      )
    }));
  };
  
  // Function to add a loan node to the session
  const addLoanNode = (nodeName, loanPerc, annualRate, tenureYears, moratoriumYears, position) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'loan',
      is_configured: true,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          loan_perc: loanPerc,
          annual_rate: annualRate,
          tenure_years: tenureYears,
          moratorium_years: moratoriumYears
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };
  
  // Function to add a depreciation node to the session
  const addDepreciationNode = (nodeName, BasicPriceWind, BasicPriceSolar, BasicPriceSolarBtm, depRateForITWDV, addnDepRate, taxRate, position, gstInput = false, gstPerc = null) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'depreciation',
      is_configured: true,
      position: position || { x: 0, y: 0 },
      node_params: [
        {
          Basic_price_wind: BasicPriceWind,
          Basic_price_solar: BasicPriceSolar,
          Basic_price_solar_btm: BasicPriceSolarBtm,
          dep_rate_for_IT_WDV: depRateForITWDV,
          addn_dep_rate: addnDepRate,
          tax_rate: taxRate,
          gst_input: gstInput,
          gst_perc: gstPerc
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };

  const addBatteryNode = (nodeName, params, inputNodeName, position) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'battery',
      is_configured: true,
      position: position || { x: 0, y: 0 },
      node_params: [
        {
          input_node_name: inputNodeName,
          gen_col_name: params.genColName,
          battery_hours: params.batteryHours,
          battery_capacity_kw: params.batteryCapacityKw,
          round_trip_efficiency: params.roundTripEfficiency,
          dod: params.dod,
          battery_capex_per_kwh: params.batteryCapexPerKwh,
          battery_om_perc: params.batteryOmPerc,
          loan_perc: params.loanPerc,
          annual_rate: params.annualRate,
          tenure_years: params.tenureYears,
          dep_rate_for_IT_WDV: params.depRateForITWDV,
          addn_dep_rate: params.addnDepRate,
          tax_rate: params.taxRate,
          gst_input: params.gstInput,
          gst_perc: params.gstPerc,
          battery_assumptions: params.batteryAssumptions,
          charge_time_slots: params.chargeTimeSlots,
          discharge_time_slots: params.dischargeTimeSlots,
        }
      ]
    };

    upsertSessionNode(newNode);
  };

  // Function to add an operational costs node to the session
  const addOperationalCostsNode = (nodeName, params, position) => {
    const newNode = {
      node_name: nodeName,
      node_type: 'operationalcosts',
      is_configured: true,
      position: position || { x: 0, y: 0 }, // Store position, default to origin if not provided
      node_params: [
        {
          no_of_bop_year: params.noOfBopYear,
          wind_bop_escalation_perc: params.windBopEscalationPerc,
          wind_escalation_perc: params.windEscalationPerc,
          solar_escalation_perc: params.solarEscalationPerc,
          discount_rate: params.discountRate,
          annual_maintenance_per_turbine_in_lakhs: params.annualMaintenancePerTurbineInLakhs,
          annual_maintenance_per_turbine_in_lakhs_with_bop: params.annualMaintenancePerTurbineInLakhsWithBop,
          annual_maintenance_per_solar_unit_in_lakhs: params.annualMaintenancePerSolarUnitInLakhs,
          insurance_percent_of_asset_cost: params.insurancePercentOfAssetCost,
          operational_charges: params.operationalCharges,
          asset_management_fees: params.assetManagementFees,
          annual_lease_charge_per_acre_solar: params.annualLeaseChargePerAcreSolar,
          annual_lease_charge_per_acre_wind: params.annualLeaseChargePerAcreWind,
          solar_acres_per_MWp: params.solarAcresPerMWp,
          wind_acres_per_WTG: params.windAcresPerWTG,
          first_year_benifits: params.firstYearBenifits,
          revenue_from_excess_banked_units_per_unit: params.revenueFromExcessBankedUnitsPerUnit,
          total_lapsed_units_for_excess_revenue_perc: params.totalLapsedUnitsForExcessRevenuePerc,
          transmission_charges_per_KWh: params.transmissionChargesPerKWh,
          wheeling_charges: params.wheelingCharges,
          electricity_duty_per_unit: params.electricityDutyPerUnit,
          banking_charge_rs: params.bankingChargeRs ?? 0,
          per_KW: params.perKW,
          per_KWh: params.perKWh,
          transmission_charges_per_Kw_per_month: params.transmissionChargesPerKwPerMonth,
          include_depreciation: params.includeDepreciation
        }
      ]
    };
    
    upsertSessionNode(newNode);
  };
  
  // Function to remove a node from the session
  const removeNode = (nodeName) => {
    console.log(`Removing node ${nodeName} from session state`);
    
    setSession((prevSession) => {
      // Filter out the node to be removed
      const updatedNodes = prevSession.nodes.filter(node => node.node_name !== nodeName);
      
      // If the removed node was the selected node, clear the selection
      const updatedSelectedNode = 
        prevSession.selected_node === nodeName ? null : prevSession.selected_node;
      
      return {
        ...prevSession,
        nodes: updatedNodes,
        selected_node: updatedSelectedNode
      };
    });
    clearNodeDirty(nodeName);
  };

  return (
    <PipelineContext.Provider value={{ 
      session, 
      dirtyNodeInputs,
      initialize_pipeline, 
      run_pipeline, 
      markNodeDirty,
      clearNodeDirty,
      clearAllDirtyNodes,
      addInputNode, 
      addNormalizerNode,
      addScalerNode,
      addCapperNode,
      addLossSubtractorNode,
      addAdderNode,
      addMatcherNode,
      addConsumptionNode,
      addWindGenerationNode,
      addSolarGenerationNode,
      addExtrapolationNode,
      addBankingNode,
      addSettleNode,
      addFinanceNode,
      addLoanNode,
      addDepreciationNode,
      addBatteryNode,
      addOperationalCostsNode,
      updateFinanceTariff,
      setNodeConfigured, 
      updateSelectedNode,
      updateNodePosition,
      batchAddNodes,
      removeNode
    }}>
      {children}
    </PipelineContext.Provider>
  );
};

// Custom hook to use the PipelineContext
export const usePipeline = () => {
  return useContext(PipelineContext);
};
